import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { UserPlus, Loader2, DollarSign, Wallet, Trash2 } from "lucide-react";

export function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [payAmount, setPayAmount] = useState("100");
  const [payNote, setPayNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/admin/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const deleteEmployee = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/employees/${confirmDel.id}`);
      toast.success("Employee deleted");
      if (active?.id === confirmDel.id) setActive(null);
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEmp = async (e) => {
    const { data } = await api.get(`/admin/employees/${e.id}`);
    setActive(data);
  };

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/employees", { name, email, password });
      toast.success("Employee account created");
      setName(""); setEmail(""); setPassword(""); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const recordPayout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/admin/employees/${active.id}/payouts`, {
        amount: parseFloat(payAmount) || 0, note: payNote,
      });
      setActive({ ...active, ...data });
      setPayNote("");
      toast.success("Payment recorded");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPaying(false);
    }
  };

  const delPayout = async (pid) => {
    await api.delete(`/admin/payouts/${pid}`);
    const { data } = await api.get(`/admin/employees/${active.id}`);
    setActive(data);
    load();
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="border border-white/10 bg-[#0A0A0B] p-6 md:col-span-1">
        <h3 className="font-display font-semibold">Create employee account</h3>
        <p className="mt-1 text-sm text-zinc-400">They log in to see who they referred & their earnings.</p>
        <Label className="mt-4 block text-zinc-400">Name</Label>
        <Input data-testid="employee-name" value={name} onChange={(e) => setName(e.target.value)}
          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Employee name" />
        <Label className="mt-4 block text-zinc-400">Email</Label>
        <Input data-testid="employee-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="employee@email.com" />
        <Label className="mt-4 block text-zinc-400">Password</Label>
        <Input data-testid="employee-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Set a password" />
        <Button data-testid="create-employee-btn" onClick={create} disabled={creating || !name || !email || password.length < 6}
          className="mt-3 w-full rounded-full bg-primary text-white hover:bg-primary/90">
          {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Create account
        </Button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0B] p-6 md:col-span-2">
        <h3 className="font-display font-semibold">Employees <span className="text-sm text-zinc-500">({employees.length})</span></h3>
        <div className="mt-4 space-y-2">
          {employees.length === 0 && <p className="text-sm text-zinc-500">No employees yet.</p>}
          {employees.map((e) => (
            <div key={e.id} data-testid={`employee-${e.id}`} onClick={() => openEmp(e)}
              className="grid w-full cursor-pointer grid-cols-12 items-center border border-white/10 bg-black px-4 py-3 text-left text-sm transition-colors hover:border-white/25">
              <div className="col-span-4 overflow-hidden">
                <p className="truncate font-medium">{e.name}</p>
                <p className="truncate font-mono text-[11px] text-zinc-500">{e.email}</p>
              </div>
              <div className="col-span-3 font-mono text-[12px] text-zinc-400">{e.referred_count} referred · {e.deposit_paid_count} paid</div>
              <div className="col-span-2 font-mono text-emerald-400">${e.earned}</div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                <span className="font-mono text-[12px] text-amber-400">owed ${e.balance}</span>
                <button
                  data-testid={`delete-employee-${e.id}`}
                  onClick={(ev) => { ev.stopPropagation(); setConfirmDel(e); }}
                  className="text-zinc-500 transition-colors hover:text-destructive"
                  title="Delete employee"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Sheet open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <SheetContent data-testid="employee-sheet" className="w-full overflow-y-auto border-l border-white/10 bg-[#08080A] text-white sm:max-w-lg">
          {active && (
            <div className="space-y-5 pt-4">
              <div>
                <h2 className="font-display text-2xl font-bold">{active.name}</h2>
                <p className="font-mono text-xs text-zinc-500">{active.email}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-white/10 bg-black p-3">
                  <p className="font-mono text-[10px] uppercase text-zinc-500">Earned</p>
                  <p className="font-display text-xl text-emerald-400">${active.earned}</p>
                </div>
                <div className="border border-white/10 bg-black p-3">
                  <p className="font-mono text-[10px] uppercase text-zinc-500">Paid</p>
                  <p className="font-display text-xl">${active.total_paid}</p>
                </div>
                <div className="border border-white/10 bg-black p-3">
                  <p className="font-mono text-[10px] uppercase text-zinc-500">Owed</p>
                  <p className="font-display text-xl text-amber-400">${active.balance}</p>
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <div className="flex items-center gap-2 text-primary"><Wallet className="h-4 w-4" />
                  <p className="font-mono text-[11px] uppercase text-zinc-400">Record a payment</p></div>
                <div className="mt-3 flex gap-2">
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-zinc-500" />
                    <Input data-testid="payout-amount" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                      className="w-28 rounded-sm border-white/10 bg-black pl-7" />
                  </div>
                  <Input data-testid="payout-note" value={payNote} onChange={(e) => setPayNote(e.target.value)}
                    placeholder="Note (optional)" className="min-w-0 flex-1 rounded-sm border-white/10 bg-black" />
                  <Button data-testid="record-payout-btn" onClick={recordPayout} disabled={paying}
                    className="rounded-sm bg-primary text-white hover:bg-primary/90">
                    {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay"}
                  </Button>
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <p className="font-mono text-[11px] uppercase text-zinc-400">Referred clients</p>
                <div className="mt-3 space-y-2">
                  {active.referrals.length === 0 && <p className="text-sm text-zinc-500">None yet.</p>}
                  {active.referrals.map((r) => (
                    <div key={r.project_id} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-200">{r.client_name}</span>
                      <span className="font-mono text-[11px] text-zinc-500">
                        {r.assigned_at ? format(parseISO(r.assigned_at), "MMM d") : ""} ·{" "}
                        {r.deposit_paid ? <span className="text-emerald-400">+${r.commission}</span> : "pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-white/10 bg-black p-4">
                <p className="font-mono text-[11px] uppercase text-zinc-400">Payment history</p>
                <div className="mt-3 space-y-2">
                  {active.payouts.length === 0 && <p className="text-sm text-zinc-500">No payments yet.</p>}
                  {active.payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-mono text-emerald-400">+ ${p.amount}</span>
                        {p.note && <span className="ml-2 text-xs text-zinc-500">{p.note}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-zinc-500">{p.paid_at ? format(parseISO(p.paid_at), "MMM d") : ""}</span>
                        <Button size="icon" variant="ghost" onClick={() => delPayout(p.id)}
                          className="h-7 w-7 text-zinc-500 hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent data-testid="delete-employee-dialog" className="border-white/10 bg-[#0A0A0B] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this employee?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently removes <span className="text-white">{confirmDel?.name}</span> ({confirmDel?.email}) and their
              payment history. Any clients they referred stay, but will no longer be linked to them. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-employee-btn" onClick={deleteEmployee}
              className="bg-destructive text-white hover:bg-destructive/90">Delete employee</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
